import * as React from "react";
import { MessageBar, MessageBarButton, MessageBarType, PrimaryButton, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { ICopilotLibraryName, ICopilotPromptDto, ICopilotPromptInput } from "../../data-structures/interfaces";
import {
    CopilotPromptCallResult,
    createGlobalCopilotPrompt,
    deleteGlobalCopilotPrompt,
    getGlobalCopilotLibraryNames,
    getGlobalCopilotPrompts,
    reorderGlobalCopilotPrompts,
    setGlobalCopilotPromptEnabled,
    updateGlobalCopilotPrompt,
} from "../../utilities/helpers/ApiHelper";
import {
    emptyCopilotPromptInput,
    formatString,
    movePromptIds,
    normalizeLibraryNames,
    toCopilotPromptInput,
} from "../../utilities/helpers/copilotPrompts";
import ConfirmDialog from "../ConfirmDialog";
import { CopilotPromptEditor } from "../panels/CopilotPromptEditor";
import { CopilotPromptList, copilotPromptMetaStyle } from "../panels/CopilotPromptList";

/**
 * The global Copilot prompts every tenant in their audience sees before its own (spec
 * 2026-10-13-copilot-chat-design.md §4 phase 3), at /copilot-prompts. The route and its navigation
 * link exist for global admins only; the API checks again.
 */
const CopilotPrompts: React.FC = () => {
    const strings = React.useContext(LanguageContext);
    const [prompts, setPrompts] = React.useState<ICopilotPromptDto[] | undefined>(undefined);
    const [loadFailed, setLoadFailed] = React.useState<boolean>(false);
    const [busy, setBusy] = React.useState<boolean>(false);
    /** Why the last change failed (the API's text, or the generic message); undefined = none. */
    const [actionError, setActionError] = React.useState<string | undefined>(undefined);
    /** Another admin changed the list (a 404 or 409): it was reloaded. */
    const [wasReloaded, setWasReloaded] = React.useState<boolean>(false);
    /** The prompt being edited; null = a new prompt; undefined = the editor is closed. */
    const [editing, setEditing] = React.useState<ICopilotPromptDto | null | undefined>(undefined);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveError, setSaveError] = React.useState<string | undefined>(undefined);
    const [deleting, setDeleting] = React.useState<ICopilotPromptDto | null>(null);

    // A failed first load keeps prompts undefined: the page then shows the error and "Try again",
    // never "No prompts yet." A failed reload keeps the list it had. True when the list was read.
    const reload = React.useCallback(async (): Promise<boolean> => {
        const list = await getGlobalCopilotPrompts();
        setPrompts(current => list ?? current);
        setLoadFailed(!list);
        return !!list;
    }, []);

    React.useEffect(() => {
        reload();
    }, [reload]);

    // Every template configuration's libraries, each with the configurations that have it, for the
    // default-library suggestions; [] when Provisioning can't say.
    const [templateLibraries, setTemplateLibraries] = React.useState<ICopilotLibraryName[]>([]);
    React.useEffect(() => {
        let cancelled = false;
        getGlobalCopilotLibraryNames().then(names => {
            if (!cancelled) setTemplateLibraries(names ?? []);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const tryAgain = () => {
        setLoadFailed(false);
        reload();
    };

    // Runs one change, then reloads. A prompt another admin deleted or reordered meanwhile (404,
    // 409) only reloads, and says so; any other failure shows the API's reason or one message.
    // When that reload fails too, the list is the old one: the change just failed (final review
    // 2026-10-19).
    const run = async (change: () => Promise<CopilotPromptCallResult<unknown>>) => {
        setBusy(true);
        setActionError(undefined);
        setWasReloaded(false);
        try {
            const result = await change();
            const changedElsewhere = !result.ok && (result.status === 404 || result.status === 409);
            if (!result.ok && !changedElsewhere) {
                setActionError(result.message ?? strings.CopilotPromptActionFailed);
            }
            const reloaded = await reload();
            if (changedElsewhere) {
                if (reloaded) {
                    setWasReloaded(true);
                } else {
                    setActionError(strings.CopilotPromptActionFailed);
                }
            }
        } finally {
            setBusy(false);
        }
    };

    const list = prompts ?? [];
    // Suggestions for the default libraries: the template configurations' libraries, then the names
    // the global prompts already use (the template's spelling wins). One global prompt can name
    // several configurations' libraries, so each template library says whose it is (2026-10-21).
    const libraryNames = normalizeLibraryNames(list.reduce<string[]>((names, prompt) => names.concat(prompt.defaultLibraries), templateLibraries.map(library => library.name)))
        .sort((a, b) => a.localeCompare(b));
    const libraryConfigurations = templateLibraries.reduce<Record<string, string[]>>(
        (byName, library) => ({ ...byName, [library.name.toLowerCase()]: library.configurations }), {});

    const openEditor = (prompt: ICopilotPromptDto | null) => {
        setSaveError(undefined);
        setEditing(prompt);
    };

    const save = async (input: ICopilotPromptInput) => {
        setIsSaving(true);
        setSaveError(undefined);
        const saved = editing ? await updateGlobalCopilotPrompt(editing.id, input) : await createGlobalCopilotPrompt(input);
        if (saved.ok) {
            setIsSaving(false);
            setWasReloaded(false);
            setEditing(undefined);
            await reload();
            return;
        }
        // The prompt is gone (another admin deleted it): close and show the list as it is now. When
        // that list can't be read, the editor stays open with the save error (final review
        // 2026-10-19): "The list has been reloaded" would be untrue.
        if (saved.status === 404 && await reload()) {
            setIsSaving(false);
            setWasReloaded(true);
            setEditing(undefined);
            return;
        }
        setIsSaving(false);
        setSaveError(saved.message ?? strings.CopilotPromptSaveFailed);
    };

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: "16px 24px", maxWidth: 960 } }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text as="h2" variant="xLarge">{strings.CopilotPromptsTitle}</Text>
                <PrimaryButton text={strings.CopilotPromptNew} disabled={busy || prompts === undefined} onClick={() => openEditor(null)} />
            </Stack>
            <Text className={copilotPromptMetaStyle}>{strings.CopilotPromptsGlobalInfo}</Text>
            {loadFailed && prompts !== undefined && <MessageBar messageBarType={MessageBarType.error}>{strings.CopilotPromptsLoadFailed}</MessageBar>}
            {actionError && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setActionError(undefined)}>
                    {actionError}
                </MessageBar>
            )}
            {wasReloaded && (
                <MessageBar messageBarType={MessageBarType.info} onDismiss={() => setWasReloaded(false)}>
                    {strings.CopilotPromptsReloaded}
                </MessageBar>
            )}
            {prompts === undefined ? (
                loadFailed ? (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        actions={<MessageBarButton onClick={tryAgain}>{strings.TryAgain}</MessageBarButton>}
                    >
                        {strings.CopilotPromptsLoadFailed}
                    </MessageBar>
                ) : (
                    <Spinner size={SpinnerSize.large} styles={{ root: { marginTop: 40 } }} />
                )
            ) : list.length === 0 ? (
                <Text>{strings.CopilotPromptsNone}</Text>
            ) : (
                <CopilotPromptList
                    prompts={list}
                    showAudience
                    busy={busy}
                    onToggleEnabled={prompt => run(() => setGlobalCopilotPromptEnabled(prompt.id, !prompt.isEnabled))}
                    onMove={(index, delta) => {
                        const ids = movePromptIds(list, index, delta);
                        if (ids) run(() => reorderGlobalCopilotPrompts(ids));
                    }}
                    onEdit={openEditor}
                    onDelete={setDeleting}
                />
            )}
            {editing !== undefined && (
                <CopilotPromptEditor
                    isGlobal
                    isNew={editing === null}
                    initial={editing ? toCopilotPromptInput(editing) : emptyCopilotPromptInput(true)}
                    libraryNames={libraryNames}
                    libraryConfigurations={libraryConfigurations}
                    isSaving={isSaving}
                    errorMessage={saveError}
                    onSave={save}
                    onCancel={() => setEditing(undefined)}
                />
            )}
            <ConfirmDialog
                isOpen={!!deleting}
                title={strings.Delete}
                message={deleting ? formatString(strings.CopilotPromptDeleteGlobalConfirm, deleting.title) : ""}
                confirmText={strings.Delete}
                onConfirm={async () => {
                    const target = deleting;
                    setDeleting(null);
                    if (target) await run(() => deleteGlobalCopilotPrompt(target.id));
                }}
                onDismiss={() => setDeleting(null)}
            />
        </Stack>
    );
};

export default CopilotPrompts;
